import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, memo } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { serverLogsAPI } from '../utils/api';
import { Button } from '../components/UI';

// Live output of every backend server, streamed while this page is open.
// Nothing is stored anywhere: lines live in this tab's memory and are gone
// when it closes. The browser keeps at most MAX_LINES.
const MAX_LINES = 2000;

const LEVELS = [
  { key: 'all',   label: 'All' },
  { key: 'info',  label: 'Info' },
  { key: 'warn',  label: 'Warnings' },
  { key: 'error', label: 'Errors' },
];

const POD_COLORS = ['#60a5fa', '#34d399', '#f472b6', '#fbbf24', '#a78bfa', '#22d3ee', '#fb923c', '#a3e635', '#f87171', '#2dd4bf'];
const podColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return POD_COLORS[h % POD_COLORS.length];
};
// backend-66bb84cbbd-tdsgh → tdsgh: the part that tells two pods apart.
const podShort = (name) => {
  const parts = String(name).split('-');
  return parts.length > 2 ? parts[parts.length - 1] : name;
};

const pad = (n, w = 2) => String(n).padStart(w, '0');
const fmtTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

// The server gives every line a stable uid; older servers may not.
const lineId = (l) => l.uid || `${l.pod}|${l.ts}|${l.text}`;

// Comparable timestamp: Kubernetes trims trailing zeros (".71812Z"), so plain
// string comparison of two stamps can be wrong.
const tsKey = (ts) => String(ts).replace(/(?:\.(\d+))?Z$/, (_, frac = '') => `.${frac.padEnd(9, '0')}Z`);

const STATUS = {
  connecting:   { label: 'Connecting…',  color: '#94a3b8', pulse: false },
  live:         { label: 'Live',         color: '#22c55e', pulse: true },
  reconnecting: { label: 'Reconnecting…', color: '#f59e0b', pulse: true },
  error:        { label: 'Disconnected', color: '#ef4444', pulse: false },
};

// ── Stream: fetch + SSE parsing, reconnecting with backoff ───────────────────
function useLogStream({ onMeta, onLines, onDropped }) {
  const [status, setStatus] = useState('connecting');
  const [error, setError]   = useState(null);
  const handlers = useRef({ onMeta, onLines, onDropped });
  handlers.current = { onMeta, onLines, onDropped };

  useEffect(() => {
    let stopped = false;
    let finished = false;   // access ended: do not reconnect
    let controller = null;
    let attempt = 0;
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const messageOf = async (res, fallback) => {
      try { return (await res.json()).message || fallback; } catch { return fallback; }
    };

    const handleFrame = (frame) => {
      let event = 'message';
      const data = [];
      for (const row of frame.split('\n')) {
        if (row.startsWith(':')) continue;   // heartbeat
        if (row.startsWith('event:')) event = row.slice(6).trim();
        else if (row.startsWith('data:')) data.push(row.slice(5).replace(/^ /, ''));
      }
      if (!data.length) return;
      let parsed;
      try { parsed = JSON.parse(data.join('\n')); } catch { return; }
      if (event === 'meta') handlers.current.onMeta(parsed);
      else if (event === 'lines') handlers.current.onLines(parsed, false);
      else if (event === 'backlog') handlers.current.onLines(parsed, true);
      else if (event === 'dropped') handlers.current.onDropped(parsed.count);
      else if (event === 'closed') { finished = true; setError(parsed.message); }
    };

    const run = async () => {
      while (!stopped && !finished) {
        controller = new AbortController();
        setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
        try {
          const res = await fetch(serverLogsAPI.streamUrl(), {
            headers: { Authorization: `Bearer ${serverLogsAPI.token()}`, Accept: 'text/event-stream' },
            signal: controller.signal,
            cache: 'no-store',
          });
          // Signed out, expired, not a SuperAdmin, or impersonating: retrying
          // cannot help. Everything else (503 during a DB blip, 429, a pod
          // going away) is retried with backoff.
          if (res.status === 401 || res.status === 403) {
            setStatus('error');
            setError(await messageOf(res, 'Only a SuperAdmin can view server logs.'));
            return;
          }
          if (!res.ok || !res.body) throw new Error(await messageOf(res, `Server answered ${res.status}`));
          attempt = 0;
          setStatus('live');
          setError(null);
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            // Proxies may re-encode line endings; SSE allows CRLF.
            buf += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');
            let i;
            while ((i = buf.indexOf('\n\n')) !== -1) {
              handleFrame(buf.slice(0, i));
              buf = buf.slice(i + 2);
            }
          }
          // The server ended the stream — usually a rollout replacing the pod.
        } catch (err) {
          if (stopped || err.name === 'AbortError') return;
          setError(err.message);
        }
        if (stopped) return;
        if (finished) { setStatus('error'); return; }
        attempt += 1;
        setStatus('reconnecting');
        await sleep(Math.min(1000 * 2 ** Math.min(attempt - 1, 4), 15000));
      }
    };
    run();
    return () => { stopped = true; controller?.abort(); };
  }, []);

  return { status, error };
}

// ── Pieces ───────────────────────────────────────────────────────────────────
function highlight(text, query) {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const out = [];
  let from = 0;
  let at = lower.indexOf(q);
  while (at !== -1) {
    if (at > from) out.push(text.slice(from, at));
    out.push(<mark key={at} className="slog-hit">{text.slice(at, at + q.length)}</mark>);
    from = at + q.length;
    at = lower.indexOf(q, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

const LogRow = memo(function LogRow({ line, query, showTime, showPod, wrap }) {
  const color = podColor(line.pod);
  return (
    <div className={`slog-row is-${line.level}`}>
      {showTime && <span className="slog-time">{fmtTime(line.ts)}</span>}
      {showPod && (
        <span className="slog-pod" style={{ color }} title={line.pod}>{podShort(line.pod)}</span>
      )}
      {line.level !== 'info' && (
        <span className={`slog-level slog-level--${line.level}`}>{line.level === 'error' ? 'ERR' : 'WRN'}</span>
      )}
      <span className={`slog-text${wrap ? '' : ' slog-text--nowrap'}`}>{highlight(line.text, query)}</span>
    </div>
  );
});

function StatusPill({ status, paused }) {
  const s = paused ? { label: 'Paused', color: '#60a5fa', pulse: false } : STATUS[status];
  return (
    <span className="slog-pill" style={{ color: s.color, borderColor: `${s.color}55`, background: `${s.color}14` }}>
      <span className={`slog-dot${s.pulse ? ' slog-dot--pulse' : ''}`} style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

function Toggle({ on, onChange, children, title }) {
  return (
    <button type="button" title={title} onClick={() => onChange(!on)}
      className={`slog-toggle${on ? ' is-on' : ''}`} aria-pressed={on}>
      <span className="slog-toggle__box">{on ? '✓' : ''}</span>{children}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ServerLogs() {
  const [lines, setLines]       = useState([]);
  const [meta, setMeta]         = useState(null);
  // Kept for this tab — survives leaving the page and coming back.
  const [query, setQuery]       = usePersistentState('serverLogs:query', '');
  const [level, setLevel]       = usePersistentState('serverLogs:level', 'all');
  const [hiddenPods, setHidden] = useState(() => new Set());
  const [onlyPod, setOnlyPod]   = useState(null);   // "only this pod"; wins over hiddenPods
  const [follow, setFollow]     = useState(true);
  const [wrap, setWrap]         = useState(true);
  const [showTime, setShowTime] = useState(true);
  const [paused, setPaused]     = useState(false);
  const [heldCount, setHeld]    = useState(0);
  const [trimmed, setTrimmed]   = useState(0);
  const [dropped, setDropped]   = useState(0);

  const linesR   = useRef([]);
  const shown    = useRef(new Set());   // ids of lines on screen or held while paused
  const held     = useRef([]);
  const seq      = useRef(0);
  const floor    = useRef('');          // history at or before this stamp was cleared or trimmed
  const seenUpTo = useRef(0);           // last row key in view when Follow turned off
  const pausedR  = useRef(false);
  const scroller = useRef(null);
  const searchRef = useRef(null);
  pausedR.current = paused;

  // Forget lines leaving the page; replayed copies of them stay out too.
  const release = (gone) => {
    for (const l of gone) {
      shown.current.delete(lineId(l));
      const k = tsKey(l.ts);
      if (k > floor.current) floor.current = k;
    }
  };

  const append = useCallback((fresh) => {
    if (!fresh.length) return;
    let next = linesR.current.concat(fresh);
    if (next.length > MAX_LINES) {
      const gone = next.slice(0, next.length - MAX_LINES);
      release(gone);
      setTrimmed(t => t + gone.length);
      next = next.slice(gone.length);
    }
    linesR.current = next;
    setLines(next);
  }, []);

  const onLines = useCallback((incoming, isBacklog) => {
    // Only history can repeat what is already on screen: the backlog sent on
    // every (re)connect, and lines a server replays from before its stream
    // opened. Live lines are never de-duplicated — a repeated line (a stack
    // frame, a retry message) is a real line.
    const fresh = [];
    for (const l of incoming) {
      const id = lineId(l);
      if ((isBacklog || l.replay) && (shown.current.has(id) || tsKey(l.ts) <= floor.current)) continue;
      shown.current.add(id);
      fresh.push({ ...l, key: ++seq.current });
    }
    if (!fresh.length) return;
    if (pausedR.current) {
      held.current.push(...fresh);
      if (held.current.length > MAX_LINES) {
        const gone = held.current.splice(0, held.current.length - MAX_LINES);
        release(gone);
        setTrimmed(t => t + gone.length);
      }
      setHeld(held.current.length);
      return;
    }
    append(fresh);
  }, [append]);

  const onDropped = useCallback((count) => setDropped(d => d + count), []);
  const { status, error } = useLogStream({ onMeta: setMeta, onLines, onDropped });

  const resume = () => {
    setPaused(false);
    const backlog = held.current;
    held.current = [];
    setHeld(0);
    append(backlog);
  };

  const clearView = () => {
    release(linesR.current);
    release(held.current);
    shown.current = new Set();
    linesR.current = [];
    held.current = [];
    setLines([]);
    setHeld(0); setTrimmed(0); setDropped(0);
  };

  // ── Derived ──
  const livePods = meta?.pods || [];
  const knownPods = useMemo(() => {
    const set = new Set(meta?.pods || []);
    for (const l of lines) set.add(l.pod);
    return [...set].sort();
  }, [meta, lines]);

  const counts = useMemo(() => {
    const c = { all: lines.length, info: 0, warn: 0, error: 0 };
    for (const l of lines) c[l.level] = (c[l.level] || 0) + 1;
    return c;
  }, [lines]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lines.filter(l =>
      (level === 'all' || l.level === level)
      && (onlyPod ? l.pod === onlyPod : !hiddenPods.has(l.pod))
      && (!q || l.text.toLowerCase().includes(q) || l.pod.toLowerCase().includes(q)));
  }, [lines, level, hiddenPods, onlyPod, query]);

  // New lines since the reader left the bottom — only those the filters show.
  const unseen = useMemo(
    () => (follow ? 0 : visible.filter(l => l.key > seenUpTo.current).length),
    [visible, follow],
  );

  // ── Scrolling: stick to the bottom unless the user scrolled up ──
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [visible, follow]);

  const stopFollowing = () => {
    seenUpTo.current = visible.length ? visible[visible.length - 1].key : seq.current;
    setFollow(false);
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom && !follow) setFollow(true);
    else if (!atBottom && follow) stopFollowing();
  };

  const jumpToLive = () => {
    setFollow(true);
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // "/" focuses the search box, Esc clears it.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '');
      if (e.key === '/' && !typing) { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) { setQuery(''); searchRef.current.blur(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const copyVisible = async () => {
    const text = visible.map(l => `${l.ts} [${l.pod}] ${l.level.toUpperCase().padEnd(5)} ${l.text}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${visible.length} line${visible.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Could not copy — the browser blocked clipboard access');
    }
  };

  const togglePod = (name) => setHidden(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  // While one pod is shown alone, clicking another chip moves the view to
  // that pod; otherwise a click hides / shows the pod as before.
  const clickPod = (name) => {
    if (onlyPod) setOnlyPod(name === onlyPod ? null : name);
    else togglePod(name);
  };
  const showOnly = (name) => setOnlyPod(prev => (prev === name ? null : name));
  const showAllPods = () => { setOnlyPod(null); setHidden(new Set()); };

  const isKube = meta?.mode === 'kubernetes';
  const showPod = knownPods.length > 1 || isKube;
  const filtered = visible.length !== lines.length;

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Server Logs</h1>
            <StatusPill status={status} paused={paused} />
          </div>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {isKube
              ? `Live output from all ${livePods.length} running backend server${livePods.length === 1 ? '' : 's'}, straight from Kubernetes.`
              : 'Live output from this backend server.'}
            {' '}Nothing is saved — close the page and it is gone.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {paused
            ? <Button onClick={resume}>▶ Resume{heldCount ? ` (${heldCount} new)` : ''}</Button>
            : <Button variant="secondary" onClick={() => setPaused(true)}>⏸ Pause</Button>}
          <Button variant="secondary" onClick={copyVisible} disabled={!visible.length}>⧉ Copy</Button>
          <Button variant="secondary" onClick={clearView} disabled={!lines.length}>✕ Clear</Button>
        </div>
      </div>

      {/* ── Notices ── */}
      {error && status !== 'live' && (
        <div className="slog-notice slog-notice--error">
          <strong>Not connected.</strong> {error}{status === 'reconnecting' ? ' Retrying…' : ''}
        </div>
      )}
      {meta?.reason && (
        <div className="slog-notice slog-notice--warn">{meta.reason}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="slog-toolbar">
        <div className="slog-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Filter lines…  ( / )" aria-label="Filter log lines" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear filter">✕</button>}
        </div>

        <div className="slog-seg" role="group" aria-label="Level">
          {LEVELS.map(l => (
            <button key={l.key} type="button" onClick={() => setLevel(l.key)}
              className={`slog-seg__btn slog-seg__btn--${l.key}${level === l.key ? ' is-active' : ''}`}>
              {l.label}<span className="slog-seg__count">{counts[l.key] || 0}</span>
            </button>
          ))}
        </div>

        <div className="slog-toggles">
          <Toggle on={follow} onChange={(v) => (v ? jumpToLive() : stopFollowing())} title="Keep the newest line in view">Follow</Toggle>
          <Toggle on={wrap} onChange={setWrap} title="Wrap long lines">Wrap</Toggle>
          <Toggle on={showTime} onChange={setShowTime} title="Show timestamps">Time</Toggle>
        </div>
      </div>

      {knownPods.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Servers</span>
          {knownPods.map(name => {
            const solo = onlyPod === name;
            const off = onlyPod ? !solo : hiddenPods.has(name);
            const live = livePods.includes(name);
            const mainTitle = onlyPod
              ? (solo ? 'Show all servers again' : `Show only ${name}`)
              : `${off ? 'Show' : 'Hide'} ${name}`;
            return (
              <span key={name} className={`slog-podchip${off ? ' is-off' : ''}${solo ? ' is-solo' : ''}`}
                style={{ '--pod': podColor(name) }}>
                <button type="button" className="slog-podchip__main" onClick={() => clickPod(name)}
                  title={`${mainTitle}${live ? '' : ' (stopped)'}`}>
                  <span className="slog-podchip__dot" />{podShort(name)}{!live && <em> stopped</em>}
                </button>
                <button type="button" className="slog-podchip__only" onClick={() => showOnly(name)}
                  aria-pressed={solo} title={solo ? 'Show all servers again' : `Show only ${name}`}>
                  only
                </button>
              </span>
            );
          })}
          {(onlyPod || hiddenPods.size > 0) && (
            <button type="button" className="slog-pods__reset" onClick={showAllPods}>Show all servers</button>
          )}
        </div>
      )}

      {/* ── Terminal ── */}
      <div className="slog-term">
        <div className="slog-term__bar">
          <span className="slog-term__lights"><i /><i /><i /></span>
          <span className="slog-term__title">
            {onlyPod
              ? `kubectl logs -f ${onlyPod}`
              : isKube ? `kubectl logs -f · ${livePods.length} pod${livePods.length === 1 ? '' : 's'}` : (meta ? `${podShort(meta.pods?.[0] || 'local')} · stdout/stderr` : 'connecting…')}
          </span>
          <span className="slog-term__stats">
            {filtered ? `${visible.length} of ${lines.length}` : lines.length} lines
            {trimmed > 0 && ` · ${trimmed} older trimmed`}
            {dropped > 0 && ` · ${dropped} skipped (tab fell behind)`}
          </span>
        </div>

        <div ref={scroller} onScroll={onScroll} className="slog-term__body" role="log" aria-live="off">
          {visible.length === 0 ? (
            <div className="slog-empty">
              {lines.length === 0
                ? (status === 'live' ? 'Connected — waiting for the next line…' : 'Connecting to the servers…')
                : 'No lines match the current filters.'}
            </div>
          ) : visible.map(l => (
            <LogRow key={l.key} line={l} query={query.trim()} showTime={showTime} showPod={showPod} wrap={wrap} />
          ))}
        </div>

        {!follow && (
          <button type="button" className="slog-jump" onClick={jumpToLive}>
            ↓ Jump to live{unseen > 0 ? ` · ${unseen} new` : ''}
          </button>
        )}
      </div>
    </div>
  );
}
